import { Center, Loader } from "@mantine/core";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";

import { isAnonymousStudioPath } from "../auth/rules/anonymousPaths";
import { ensureSession } from "../auth/rules/ensureSession";
import type { RouterContext } from "../routerContext";

/**
 * Route is the root route. It resolves the Ory session once in beforeLoad
 * (outside React, before any component mounts) and hydrates the injected
 * identity store, then exposes the resolved session on the router context so
 * child route guards (the _app shell) can redirect anonymous users. Route
 * protection itself lives in _app; the root only hydrates + carries the session.
 * The theme + query providers wrap the RouterProvider in main.tsx, sharing the
 * same module-scoped store that beforeLoad writes through context.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    // The embed and share pages are anonymous (their identity comes from the
    // publishable-key org lookup or the share token, not Ory), so skip the whoami
    // entirely — it would otherwise fire a needless Ory call on a page whose reads
    // authenticate with a scoped credential, and mix the visitor's own identity into
    // an anonymous surface. The match is segment-aware so /studio/embedded does not
    // slip through.
    if (isAnonymousStudioPath(location.pathname)) {
      return { session: null };
    }

    return { session: await ensureSession(context.identityStore) };
  },
  component: RootRoute,
  pendingComponent: RootPending,
});

function RootRoute() {
  return <Outlet />;
}

function RootPending() {
  return (
    <Center mih="100vh">
      <Loader />
    </Center>
  );
}
